import {
  computed, inject, onBeforeMount, onBeforeUnmount, provide, reactive, readonly, ref, Ref, watch,
} from 'vue';

export const CurrentStepSymbol = Symbol('Current Step');
export const StepCountSymbol = Symbol('Step Count');
export const RegisterStepSymbol = Symbol('Register Step');
export const UnregisterStepSymbol = Symbol('Unregister Step');
export const ControlsSymbol = Symbol('Step controls');

interface StepperControls {
  nextStep: () => void;
  prevStep: () => void;
  setStep: (step: number) => void;
  lastStep: () => void;
}

/**
 * Provides the state object for creating a stepper.
 */
export function provideStepper() {
  const currentStep = ref(1);
  const maxStep = ref(0);

  provide(CurrentStepSymbol, readonly(currentStep));
  provide(StepCountSymbol, readonly(maxStep));

  provide(RegisterStepSymbol, () => {
    maxStep.value += 1;
  });
  provide(UnregisterStepSymbol, () => {
    maxStep.value -= 1;
  });

  const controls = reactive<StepperControls>({
    nextStep() {
      if (currentStep.value < maxStep.value) {
        currentStep.value += 1;
      }
    },
    prevStep() {
      if (currentStep.value > 1) {
        currentStep.value -= 1;
      }
    },
    setStep(n: number) {
      if (n > 0 && n < maxStep.value) {
        currentStep.value = n;
      }
    },
    lastStep() {
      currentStep.value = maxStep.value;
    },
  });
  provide(ControlsSymbol, controls);

  return {
    currentStep,
    maxStep,
    ...controls,
  };
}

/**
 * Returns the functions require to control the stepper,
 * but does not register the caller as being a step.
 *
 * Use this for navigator purposes, etc.
 */
export function useStepperNavigation(step?: Ref) {
  const currentStep = inject(CurrentStepSymbol) as Ref<number>;
  const maxStep = inject(StepCountSymbol) as Ref<number>;
  const controls = inject(ControlsSymbol) as StepperControls;
  const isVisible = computed(() => step && currentStep.value === step.value);

  return {
    currentStep,
    maxStep,
    isVisible,

    ...controls,
  };
}

/**
 * Registers the caller as a step with step index `step`.
 *
 * Returns at least the `isVisible` computed variable that will
 * show you if you should render.
 *
 * @param step Your step index
 */
export function useStepper(step?: Ref) {
  const register = inject(RegisterStepSymbol, () => null);
  onBeforeMount(register);
  const unregister = inject(UnregisterStepSymbol, () => null);
  onBeforeUnmount(unregister);

  const nav = useStepperNavigation(step);
  return {
    ...nav,
  };
}

/**
 * Provides a convenient way to skip a step given a condition.
 *
 * @example
 * useStepSkipper(myStep, () => this.shouldSkip);
 *
 * @param step Your step index
 * @param skipper Callback function to decide whether to skip or not. Returning true will skip the step, false will not.
 */
export function useStepSkipper(step: Ref<number>, skipper: () => boolean) {
  const nav = useStepperNavigation();

  watch([nav.currentStep, step], ([navStep, myStep], [prevNavStep]) => {
    // Ignore event if this isn't the active step
    if (navStep !== myStep) return;

    if (skipper()) {
      if (prevNavStep <= navStep) {
        nav.nextStep();
      } else {
        nav.prevStep();
      }
    }
  });
}
